import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

/**
 * Map a `data-anim` / `data-anim-children` effect keyword to its FROM state.
 * The TO state is always the element's natural layout (opacity 1, no offset).
 */
function fromState(effect) {
    switch (effect) {
        case 'pop':
            return { opacity: 0, scale: 0.8, y: 30 }
        case 'left':
            return { opacity: 0, x: -50 }
        case 'right':
            return { opacity: 0, x: 50 }
        case 'fade':
            return { opacity: 0 }
        default: // 'up'
            return { opacity: 0, y: 40 }
    }
}

const easeFor = (effect) => (effect === 'pop' ? 'back.out(1.6)' : 'power3.out')

const TO_BASE = { opacity: 1, x: 0, y: 0, scale: 1 }

/**
 * Wrap a tween's TO-vars so GSAP coexists with CSS transitions / Tailwind hovers.
 *
 *  • `clearProps: 'transform'` strips the inline transform GSAP leaves behind once
 *    the tween finishes. Without it that inline transform permanently beats
 *    class-based utilities (e.g. `hover:scale-105`), since inline always wins over
 *    a class selector — the hover would silently stop working after the animation.
 *  • We deliberately do NOT clear `opacity`: intro elements are pre-hidden by
 *    `.has-js [data-hero] { opacity: 0 }` (which stays active for the page's life).
 *    Dropping the inline `opacity:1` would let that rule reapply and make the
 *    element vanish. No hover here animates opacity, so keeping the final inline
 *    opacity is harmless and keeps the element visible.
 *  • Forcing `transition: none` for the tween's lifetime stops a CSS
 *    `transition` / `transition-transform` from easing — and therefore lagging —
 *    every per-frame value GSAP writes, which otherwise smears the motion.
 *
 * Only for one-shot tweens (intro + reveals). Continuous tweens (the parallax
 * scrub) must keep their inline values, so they don't use this.
 */
function guard(toVars) {
    return {
        ...toVars,
        clearProps: 'transform',
        onStart() {
            this.targets().forEach((t) => t.style.setProperty('transition', 'none', 'important'))
        },
        onComplete() {
            this.targets().forEach((t) => t.style.removeProperty('transition'))
        },
    }
}

/**
 * Intro timeline — runs once on load for everything above the fold
 * (header, hero copy, the banner render, scroll cue, floating buttons).
 * These elements are pre-hidden via CSS (`.has-js [data-hero]`) to avoid a flash.
 * Returns the timeline, so callers can wait for the intro to finish.
 */
function playIntro() {
    const tl = gsap.timeline({ defaults: { ease: 'power3.out' } })

    // Shared because the hero callouts below start where the render lands. Left
    // as two literals they would drift apart the first time either is retimed.
    const BANNER_DURATION = 1.6

    // The banner render settles out of a slow zoom. It ends fully opaque: the
    // 0.95 this used to hold suited the decorative flower it replaced, but reads
    // as a washed-out photo now that the image fills the section.
    const banner = document.querySelector('[data-hero="img"]')
    if (banner) {
        tl.fromTo(
            banner,
            { opacity: 0, scale: 1.12 },
            guard({ opacity: 1, scale: 1, duration: BANNER_DURATION, ease: 'power2.out' }),
            0
        )
    }

    const nav = gsap.utils.toArray('[data-hero="nav"]')
    if (nav.length) {
        tl.fromTo(nav, { opacity: 0, y: -20 }, guard({ opacity: 1, y: 0, duration: 0.8, stagger: 0.1 }), 0.1)
    }

    const heroText = gsap.utils.toArray('[data-hero="text"]')
    if (heroText.length) {
        tl.fromTo(
            heroText,
            { opacity: 0, y: 40 },
            guard({ opacity: 1, y: 0, duration: 1, stagger: 0.12 }),
            0.35
        )
    }

    const float = document.querySelector('[data-float]')
    if (float) {
        // These buttons keep a `transition-transform hover:scale-105` — guard() clears
        // GSAP's inline transform afterwards so the hover keeps working.
        tl.fromTo(
            float.children,
            { opacity: 0, scale: 0 },
            guard({ opacity: 1, scale: 1, duration: 0.6, ease: 'back.out(2)', stagger: 0.12 }),
            0.9
        )
    }

    // Plans-page hero callouts. They annotate the render, so they wait for it:
    // starting them at BANNER_DURATION means nothing is labelling a picture that
    // is still zooming. Marker and card are one element, so the pin can never
    // arrive before the box it belongs to.
    //
    // Same fade-up as everything else, in the same pixel units — just a much
    // shorter one. These are anchored to points on the render, so travel works
    // against them: at the 40px the hero copy uses they read as sliding in from
    // somewhere else, where 2px reads as settling onto the spot they already
    // belong to. The stagger matches the rest of the intro; three arriving
    // together would read as one flash.
    const callouts = gsap.utils.toArray('[data-hero="callout"]')
    if (callouts.length) {
        tl.fromTo(
            callouts,
            { opacity: 0, y: 2 },
            guard({ opacity: 1, y: 0, duration: 0.8, stagger: 0.12 }),
            BANNER_DURATION
        )
    }

    // Last on the timeline, so it waits out every tween above — see the
    // `is-settled` rule in css/base/header.css for why the blur has to.
    tl.call(() => nav.forEach((el) => el.classList.add('is-settled')))

    return tl
}

/** Single-element scroll reveals: `data-anim="up|fade|pop|left|right"`. */
function initReveals() {
    gsap.utils.toArray('[data-anim]').forEach((el) => {
        const effect = el.dataset.anim || 'up'
        gsap.fromTo(
            el,
            fromState(effect),
            guard({
                ...TO_BASE,
                duration: 0.9,
                ease: easeFor(effect),
                scrollTrigger: { trigger: el, start: 'top 85%', once: true },
            })
        )
    })
}

/** Staggered children: `data-anim-children="up|fade|pop|left|right"`. */
function initStaggers() {
    gsap.utils.toArray('[data-anim-children]').forEach((group) => {
        const effect = group.dataset.animChildren || 'up'
        const targets = group.children.length ? group.children : [group]
        gsap.fromTo(
            targets,
            fromState(effect),
            guard({
                ...TO_BASE,
                duration: 0.8,
                stagger: 0.12,
                ease: easeFor(effect),
                scrollTrigger: { trigger: group, start: 'top 80%', once: true },
            })
        )
    })
}

/** Scrubbed parallax for background imagery: `data-parallax="<distance %>"`. */
function initParallax() {
    gsap.utils.toArray('[data-parallax]').forEach((el) => {
        const distance = parseFloat(el.dataset.parallax) || 10
        gsap.set(el, { scale: 1.15 })
        gsap.fromTo(
            el,
            { yPercent: -distance },
            {
                yPercent: distance,
                ease: 'none',
                scrollTrigger: {
                    trigger: el.closest('section') || el,
                    start: 'top bottom',
                    end: 'bottom top',
                    scrub: true,
                },
            }
        )
    })
}

/** Toggle the header's solid state after a small scroll offset. */
function initHeaderState() {
    const header = document.querySelector('#main-header')
    if (!header) return

    ScrollTrigger.create({
        start: 0,
        end: 'max',
        onUpdate: (self) => header.classList.toggle('is-scrolled', self.scroll() > 80),
    })
}

/**
 * Wire up every site animation. Safe to call once on DOM ready.
 * Sets `window.__animReady` so the inline <head> fallback knows GSAP took over.
 * Returns a promise that settles once the intro has finished — straight away
 * under reduced motion, where there is no intro to wait for.
 */
export function initScrollAnimations() {
    const root = document.documentElement
    window.__animReady = true

    // Honour reduced-motion: reveal everything, skip all motion.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        root.classList.remove('has-js')
        return Promise.resolve()
    }

    const intro = playIntro()
    initReveals()
    initStaggers()
    initParallax()
    initHeaderState()

    // Re-measure once fonts/images settle so triggers line up correctly.
    window.addEventListener('load', () => ScrollTrigger.refresh())

    // Settle with nothing: a GSAP timeline is itself a thenable, and passing it on
    // as the value would have the promise try to adopt it.
    return intro.then(() => {})
}
